import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import axios from 'axios';

// Components
import AdminModal from './AdminModal';

// Utils

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  maxWidth: '90%',
  bgcolor: 'background.paper',
  border: '1px solid #ccc',
  borderRadius: '8px',
  boxShadow: 24,
  p: 4,
};

// Create axios instance with authentication
export const createAuthenticatedAxios = () => {
  const token = localStorage.getItem('adminToken');
  return axios.create({
    baseURL: '',
    headers: {
      'X-API-Key': token
    }
  });
};

function PasswordProtectedAdmin({ open, onClose }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  // Check if we have a token already
  React.useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      // You could validate the token here if needed
      setAuthenticated(true);
    }
  }, []);

  // Handle password change
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setError(''); // Clear error when user types
  };

  // Handle password submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use the new login endpoint
      const response = await axios.post(`/api/admin/login`, {
        password: password
      });

      // Check for token in the response
      if (response.data.access_token) {
        // Store the token
        localStorage.setItem('adminToken', response.data.access_token);
        setAuthenticated(true);
      } else {
        setError('Invalid response from server. Please try again.');
        setPassword('');
      }
    } catch (err) {
      console.error('Authentication error:', err);
      
      // More detailed error handling
      if (err.response) {
        // Server responded with an error
        if (err.response.status === 401) {
          setError('Invalid password. Please try again.');
        } else if (err.response.status === 429) {
          setError('Too many failed attempts. Please try again later.');
        } else {
          setError(`Server error: ${err.response.status}. Please try again.`);
        }
      } else if (err.request) {
        // No response received
        setError('No response from server. Please check your connection.');
      } else {
        // Something else went wrong
        setError('An error occurred. Please try again.');
      }
      
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setAuthenticated(false);
  };

  // Handle closing all modals
  const handleClose = () => {
    setPassword('');
    setError('');
    if (!authenticated) {
      // Only clear authentication if we're canceling login
      // If we were already authenticated, keep that state
      setAuthenticated(false);
      localStorage.removeItem('adminToken');
    }
    onClose();
  };

  // If authenticated, show the actual admin modal
  if (authenticated) {
    return (
      <AdminModal 
        open={open} 
        onClose={handleClose} 
        onLogout={handleLogout} 
        authenticatedAxios={createAuthenticatedAxios()} 
      />
    );
  }

  // Otherwise, show the password form
  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" component="h2" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <Lock sx={{ mr: 1 }} />
          Admin Access
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <TextField
            label="Admin Password"
            type="password"
            value={password}
            onChange={handlePasswordChange}
            fullWidth
            margin="normal"
            variant="outlined"
            autoFocus
            disabled={loading}
          />
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              onClick={handleClose} 
              variant="outlined"
              disabled={loading}
            >
              Cancel
            </Button>
            
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading || !password.trim()}
              startIcon={loading && <CircularProgress size={20} color="inherit" />}
            >
              {loading ? 'Verifying...' : 'Access Admin Panel'}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
}

export default PasswordProtectedAdmin;